import React, { Component, createRef, ReactElement } from 'react';

import { PhotoUploaderAPI } from '@philips-emr/tws-components-api';
import { Expression, PhotoSource } from '@philips-emr/tws-constants';
import { AwesomeComponent, ComponentType } from '@philips-emr/tws-core';
import { bool, eventListener, instanceOf, oneOf, string } from '@philips-emr/tws-core/lib/component/prop-types';
import { ChangeEvent } from '@philips-emr/tws-core/lib/component/types';
import { classNames, enumValues, isFunction } from '@philips-emr/tws-utils';

import { Button, Image } from '..';

import './photo-uploader.css';

export interface PhotoUploaderProps {
  className?: string;
  enabled: boolean;
  onChange: (event: ChangeEvent<Blob>) => void;
  photoVisible: boolean;
  readOnly: boolean;
  source: PhotoSource;
  takePhotoExpression: string;
  value: Blob;
}

interface PhotoUploaderState {
  photoSrc?: string | null;
}

interface InputFileProperties {
  capture?: string;
}

@AwesomeComponent({ api: PhotoUploaderAPI, type: ComponentType.DATA_ENTRY })
class PhotoUploader extends Component<PhotoUploaderProps, PhotoUploaderState> {
  state: PhotoUploaderState = {};

  private fileReader = new FileReader();
  private inputRef = createRef<HTMLInputElement>();

  static propTypes = {
    className: string,
    enabled: bool,
    onChange: eventListener,
    photoVisible: bool,
    readOnly: bool,
    source: oneOf(enumValues(PhotoSource)),
    takePhotoExpression: string,
    value: instanceOf(Blob),
  };

  static defaultProps = {
    enabled: true,
    photoVisible: true,
    source: PhotoSource.ALL,
    takePhotoExpression: Expression.TAKE_PHOTO,
  };

  componentDidMount(): void {
    const { value } = this.props;

    this.fileReader.onload = (event) => {
      const src = event.target ? (event.target.result as string) : undefined;

      this.setState({ photoSrc: src });
    };

    if (value) {
      this.fileReader.readAsDataURL(value);
    }
  }

  componentDidUpdate(prevProps: PhotoUploaderProps): void {
    const { value } = this.props;

    if (value !== prevProps.value) {
      this.fileReader.readAsDataURL(value);
    }
  }

  componentWillUnmount(): void {
    this.fileReader.abort();
  }

  getClassName(): string {
    const { className } = this.props;

    return classNames({
      'c-photo-uploader': true,
      [`${className}`]: Boolean(className),
    });
  }

  onPhotoChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { onChange, value: oldValue } = this.props;

    if (isFunction(onChange)) {
      onChange(new ChangeEvent({ oldValue, value: event.target.files?.[0] }));
    }
  };

  onTakePhotoClick = (): void => {
    this.inputRef.current?.click();
  };

  renderButton(): ReactElement | null {
    const { enabled, readOnly, takePhotoExpression } = this.props;

    if (readOnly) {
      return null;
    }

    return (
      <Button
        enabled={enabled}
        className="c-photo-uploader__button"
        onClick={this.onTakePhotoClick}
        value={takePhotoExpression}
      />
    );
  }

  renderInput(): ReactElement | null {
    const { readOnly, source } = this.props;

    if (readOnly) {
      return null;
    }

    const extraFileProperties: InputFileProperties = { };

    if (source !== PhotoSource.ALL) {
      extraFileProperties.capture = source;
    }

    return (
      <input
        accept="image/*"
        className="c-photo-uploader__file"
        onChange={this.onPhotoChange}
        ref={this.inputRef}
        type="file"
        {...extraFileProperties}
      />
    );
  }

  renderPhoto(): ReactElement | null {
    const { photoVisible } = this.props;
    const { photoSrc } = this.state;

    if (!photoVisible || !photoSrc) {
      return null;
    }

    return (
      <Image
        alt={Expression.SELECTED_PHOTO as string}
        className="c-photo-uploader__preview"
        src={photoSrc}
        maxWidth="20rem"
      />
    );
  }

  render(): ReactElement | null {
    return (
      <div className={this.getClassName()}>
        {this.renderButton()}
        {this.renderInput()}
        {this.renderPhoto()}
      </div>
    );
  }
}

export default PhotoUploader;
